import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth";
import { supabase } from "../supabaseClient";
import { ACCEPTED_IMAGE_TYPES, uploadImage } from "../cloudinaryUpload";
import {
  PreferredDaysEditor,
  normalizePreferredDays,
} from "../preferredDays";
import KitColorsEditor, { normalizeKitColors } from "./KitColorsEditor";
import NotifyOptIn from "./NotifyOptIn";
import {
  notifyPrefsToProfilePatch,
  notifyPrefsFromProfile,
  useNotifyChannels,
} from "../notifySettings";
import { saveDraft } from "../utils/drafts";
import { friendlyError } from "../utils/errors";

const EMPTY_FORM = {
  league_id: "",
  team_name: "",
  owner_name: "",
  phone: "",
  email: "",
};

function validateStep(stepId, form, preferredDays = []) {
  const errors = {};
  if (stepId === "league") {
    if (!form.league_id) errors.league_id = "Selecciona una liga.";
  } else if (stepId === "contact") {
    if (!form.owner_name.trim()) errors.owner_name = "El nombre del responsable es obligatorio.";
    const phone = form.phone.trim();
    if (!phone) {
      errors.phone = "El teléfono es obligatorio.";
    } else if (phone.replace(/\D/g, "").length < 7) {
      errors.phone = "Ingresa un teléfono válido.";
    }
    if (!form.email.trim()) {
      errors.email = "El correo es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Ingresa un correo válido.";
    }
  } else if (stepId === "days") {
    const invalidDay = preferredDays.find((d) => d.preferred_time_type && !d.preferred_time);
    if (invalidDay) {
      errors.days = "Ingresa la hora para los días seleccionados.";
    }
  } else if (stepId === "team") {
    if (!form.team_name.trim()) errors.team_name = "El nombre del equipo es obligatorio.";
  }
  return errors;
}

const TeamRegistrationForm = ({ leagues, onSuccess, onCancel, draftId, initialData }) => {
  const { user, profile, refreshProfile } = useAuth();
  const { channels: notifyChannels } = useNotifyChannels();
  
  const [form, setForm] = useState(initialData?.form || EMPTY_FORM);
  const [preferredDays, setPreferredDays] = useState(initialData?.preferredDays || []);
  const [primaryKitColors, setPrimaryKitColors] = useState(initialData?.primaryKitColors || []);
  const [secondaryKitColors, setSecondaryKitColors] = useState(initialData?.secondaryKitColors || []);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPhoneChannel, setNotifyPhoneChannel] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const fileInputRef = useRef(null);

  const [currentStepIndex, setCurrentStepIndex] = useState(initialData?.currentStepIndex || 0);
  const activeDraftId = useRef(draftId || Date.now().toString());

  // Auto-save draft effect
  useEffect(() => {
    if (!user) return;
    
    // Only save once the user has actually started: owner name/phone/email are
    // prefilled from the profile, so they don't count as progress (opening and
    // closing the form used to leave an empty "Equipo sin nombre" draft).
    if (currentStepIndex === 0 && !form.league_id && !form.team_name) {
      return;
    }

    const draftData = {
      form,
      preferredDays,
      primaryKitColors,
      secondaryKitColors,
      currentStepIndex,
    };
    
    saveDraft(user.id, activeDraftId.current, draftData);
  }, [user, form, preferredDays, primaryKitColors, secondaryKitColors, currentStepIndex]);

  // Prefill contact fields from the account, but only fill blanks and never
  // overwrite typing: user/profile objects are replaced on every auth token
  // refresh, which used to reset what the owner had entered.
  useEffect(() => {
    if (!user || initialData) return;
    setForm((prev) => ({
      ...prev,
      email: prev.email || user.email || "",
      owner_name: prev.owner_name || profile?.full_name || "",
      phone: prev.phone || profile?.phone || "",
    }));
  }, [user, profile, initialData]);

  useEffect(() => {
    const prefs = notifyPrefsFromProfile(profile, notifyChannels);
    setNotifyEmail(prefs.email);
    setNotifyPhoneChannel(prefs.phoneChannel);
  }, [profile, notifyChannels]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const selectedLeague = leagues.find((l) => l.id === form.league_id);
  const hasMultipleDays = selectedLeague?.active_days?.length > 1;

  const steps = [
    { id: "league", label: "Liga" },
    { id: "contact", label: "Contacto" }
  ];
  if (hasMultipleDays) {
    steps.push({ id: "days", label: "Días" });
  }
  steps.push({ id: "team", label: "Equipo" });

  const currentStep = steps[currentStepIndex];

  // Make sure we don't end up on an invalid step index if we changed leagues
  useEffect(() => {
    if (currentStepIndex >= steps.length) {
      setCurrentStepIndex(steps.length - 1);
    }
  }, [steps.length, currentStepIndex]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setSubmitErr(null);
  };

  const handleNext = () => {
    if (!currentStep) return;
    const errors = validateStep(currentStep.id, form, preferredDays);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setFieldErrors({});
    }
  };

  const handleDayToggle = (dayValue) => {
    setFieldErrors((prev) => {
      if (!prev.days) return prev;
      const next = { ...prev };
      delete next.days;
      return next;
    });
    setPreferredDays((prev) => {
      const existing = prev.find((d) => d.day_of_week === dayValue);
      if (existing) return prev.filter((d) => d.day_of_week !== dayValue);
      return [
        ...prev,
        { day_of_week: dayValue, preferred_time_type: "", preferred_time: "" },
      ];
    });
  };

  const updateDayField = (dayValue, field, value) => {
    setFieldErrors((prev) => {
      if (!prev.days) return prev;
      const next = { ...prev };
      delete next.days;
      return next;
    });
    setPreferredDays((prev) =>
      prev.map((d) => {
        if (d.day_of_week !== dayValue) return d;
        const updated = { ...d, [field]: value };
        if (field === "preferred_time_type" && !value) {
          updated.preferred_time = "";
        }
        return updated;
      })
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (currentStepIndex !== steps.length - 1) {
      handleNext();
      return;
    }

    setSubmitErr(null);
    if (!user) {
      setSubmitErr("Inicia sesión para enviar una solicitud.");
      return;
    }

    const errors = validateStep("team", form, preferredDays);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      let logoUrl = null;
      if (logoFile) {
        const uploaded = await uploadImage(logoFile, {
          purpose: "registration-logo",
        });
        logoUrl = uploaded.url;
      }

      const preferredDaysPayload = hasMultipleDays 
        ? normalizePreferredDays(preferredDays) 
        : [];

      const { error } = await supabase.from("team_registrations").insert({
        user_id: user.id,
        league_id: form.league_id,
        team_name: form.team_name.trim(),
        owner_name: form.owner_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        logo_url: logoUrl,
        preferred_days: preferredDaysPayload,
        primary_kit_colors: normalizeKitColors(primaryKitColors),
        secondary_kit_colors: normalizeKitColors(secondaryKitColors),
        status: "pending",
      });

      if (error) {
        if (/pending|check|policy|row-level security/i.test(error.message || "")) {
          throw new Error("No se pudo enviar. Revisa que la liga siga abierta o que no hayas alcanzado el límite de 5 pendientes.");
        }
        throw error;
      }

      const phone = form.phone.trim();
      const ownerName = form.owner_name.trim();
      const profilePatch = {
        ...notifyPrefsToProfilePatch(
          { email: notifyEmail, phoneChannel: notifyPhoneChannel },
          notifyChannels
        ),
      };
      if (phone && !profile?.phone) profilePatch.phone = phone;
      if (ownerName && !profile?.full_name) profilePatch.full_name = ownerName;
      await supabase.from("profiles").update(profilePatch).eq("id", user.id);
      if (refreshProfile) await refreshProfile(user.id);

      onSuccess?.(activeDraftId.current);
    } catch (err) {
      console.error(err);
      setSubmitErr(friendlyError(err, "No se pudo enviar la solicitud. Inténtalo de nuevo."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentStep) return null;

  return (
    <div className="reg-wizard">
      <div className="reg-stepper">
        {steps.map((step, idx) => {
          const isActive = idx === currentStepIndex;
          const isCompleted = idx < currentStepIndex;
          return (
            <div key={step.id} className={`reg-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}>
              <div className="reg-step-number">{idx + 1}</div>
              <div className="reg-step-label">{step.label}</div>
            </div>
          );
        })}
      </div>

      <form className="reg-form" onSubmit={onSubmit} noValidate>
        {currentStep.id === "league" && (
          <div className="reg-step-content">
            <label className="reg-field">
              <span className="reg-label">
                Liga <span className="reg-req">*</span>
              </span>
              <select
                name="league_id"
                value={form.league_id}
                onChange={onChange}
                className={fieldErrors.league_id ? "invalid" : ""}
                disabled={submitting}
                required
              >
                <option value="">Selecciona una liga…</option>
                {leagues.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.league_name}
                    {l.season ? ` (${l.season})` : ""}
                  </option>
                ))}
              </select>
              {fieldErrors.league_id && (
                <span className="reg-field-error">{fieldErrors.league_id}</span>
              )}
            </label>
          </div>
        )}

        {currentStep.id === "contact" && (
          <div className="reg-step-content">
            <label className="reg-field">
              <span className="reg-label">
                Nombre del responsable <span className="reg-req">*</span>
              </span>
              <input
                type="text"
                name="owner_name"
                value={form.owner_name}
                onChange={onChange}
                className={fieldErrors.owner_name ? "invalid" : ""}
                disabled={submitting}
                maxLength={80}
                required
              />
              {fieldErrors.owner_name && (
                <span className="reg-field-error">{fieldErrors.owner_name}</span>
              )}
            </label>

            <label className="reg-field">
              <span className="reg-label">
                Teléfono <span className="reg-req">*</span>
              </span>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={onChange}
                className={fieldErrors.phone ? "invalid" : ""}
                disabled={submitting}
                maxLength={30}
                required
              />
              {fieldErrors.phone && (
                <span className="reg-field-error">{fieldErrors.phone}</span>
              )}
            </label>

            <label className="reg-field">
              <span className="reg-label">
                Correo <span className="reg-req">*</span>
              </span>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className={fieldErrors.email ? "invalid" : ""}
                disabled={submitting}
                readOnly={Boolean(user?.email)}
                maxLength={120}
                required
              />
              {fieldErrors.email && (
                <span className="reg-field-error">{fieldErrors.email}</span>
              )}
            </label>

            <NotifyOptIn
              channels={notifyChannels}
              email={notifyEmail}
              phoneChannel={notifyPhoneChannel}
              onEmailChange={setNotifyEmail}
              onPhoneChannelChange={setNotifyPhoneChannel}
              disabled={submitting}
            />
          </div>
        )}

        {currentStep.id === "days" && (
          <div className="reg-step-content">
            <PreferredDaysEditor
              preferredDays={preferredDays}
              onToggleDay={handleDayToggle}
              onUpdateDay={updateDayField}
              onClear={() => setPreferredDays([])}
              disabled={submitting}
              availableDays={selectedLeague?.active_days}
            />
            {fieldErrors.days && (
              <span className="reg-field-error" style={{ marginTop: '-8px', display: 'block' }}>
                {fieldErrors.days}
              </span>
            )}
          </div>
        )}

        {currentStep.id === "team" && (
          <div className="reg-step-content">
            <label className="reg-field">
              <span className="reg-label">
                Nombre del equipo <span className="reg-req">*</span>
              </span>
              <input
                type="text"
                name="team_name"
                value={form.team_name}
                onChange={onChange}
                className={fieldErrors.team_name ? "invalid" : ""}
                disabled={submitting}
                maxLength={80}
                required
              />
              {fieldErrors.team_name && (
                <span className="reg-field-error">{fieldErrors.team_name}</span>
              )}
            </label>

            <div className="reg-field">
              <KitColorsEditor
                label="Uniforme principal (opcional)"
                colors={primaryKitColors}
                onChange={setPrimaryKitColors}
                helpText="Hasta 3 colores del uniforme."
                disabled={submitting}
              />
            </div>

            <div className="reg-field">
              <KitColorsEditor
                label="Uniforme alterno (opcional)"
                colors={secondaryKitColors}
                onChange={setSecondaryKitColors}
                disabled={submitting}
              />
            </div>

            <div className="reg-field">
              <span className="reg-label">Logo del equipo (opcional)</span>
              <div className="reg-logo-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_TYPES}
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  disabled={submitting}
                  className="reg-file"
                />
                {logoPreview && (
                  <div className="reg-logo-preview-wrap">
                    <img
                      src={logoPreview}
                      alt="Vista previa del logo"
                      className="reg-logo-preview"
                    />
                    <button
                      type="button"
                      className="reg-logo-clear"
                      onClick={() => {
                        setLogoFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      disabled={submitting}
                    >
                      Quitar
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {submitErr && (
          <div className="reg-status error" role="alert">
            {submitErr}
          </div>
        )}

        <div className="reg-wizard-actions">
          {currentStepIndex > 0 && (
            <button
              type="button"
              className="reg-btn-secondary"
              onClick={handleBack}
              disabled={submitting}
            >
              Atrás
            </button>
          )}
          
          <button type="submit" className="reg-submit" disabled={submitting}>
            {currentStepIndex === steps.length - 1 
              ? (submitting ? "Enviando…" : "Enviar solicitud")
              : "Siguiente"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamRegistrationForm;
